import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { jsPDF } from 'jspdf';
import { firstValueFrom } from 'rxjs';
import { MaintenanceService } from '../../../Services/maintenance.service';
import { CustomerService } from '../../../Services/customer.service';
import { Customer, CustomerContactPerson } from '../../../Models/customer.model';
import {
  Maintenance,
  MaintenanceBilling,
  MaintenanceBillingLine,
  MaintenanceBillingLineSourceType,
  MaintenanceBillingOverview,
  MaintenanceBillingStatus,
  MaintenanceStatus
} from '../../../Models/maintenance.model';
import { drawPdfHeader, drawSectionTitle } from '../shared/pdf-branding.util';

type BillingFilter = 'open' | 'billed' | 'all';

interface CustomerBillingGroup {
  key: string;
  customerId?: number | null;
  customerName: string;
  billable: MaintenanceBillingOverview[];
  pending: MaintenanceBillingOverview[];
  totalNet: number;
  openCount: number;
  billedCount: number;
  unpricedCount: number;
}

interface InvoiceTemplateDraft {
  invoiceName: string;
  invoiceAddress: string;
  shippingName: string;
  shippingAddress: string;
  customerNumber: string;
  vatId: string;
  orderNumber: string;
  orderDate: string | null;
  projectNumber: string;
  projectManagerName: string;
  projectManagerPhone: string;
  projectManagerEmail: string;
  invoiceEmail: string;
  paymentTerms: string;
  deliveryTerms: string;
  invoiceNote: string;
  invoiceContactId: number | null;
  shippingContactId: number | null;
  projectContactId: number | null;
  includeServiceAppendix: boolean;
}

@Component({
  selector: 'app-commercial-billing',
  templateUrl: './commercial-billing.component.html',
  styleUrls: ['./commercial-billing.component.css']
})
export class CommercialBillingComponent implements OnInit {
  readonly MaintenanceBillingStatus = MaintenanceBillingStatus;
  readonly MaintenanceBillingLineSourceType = MaintenanceBillingLineSourceType;
  readonly MaintenanceStatus = MaintenanceStatus;

  loading = true;
  saving = false;
  selectedLoading = false;
  filter: BillingFilter = 'open';
  items: MaintenanceBillingOverview[] = [];
  groups: CustomerBillingGroup[] = [];
  selectedCustomer: CustomerBillingGroup | null = null;
  selected: MaintenanceBillingOverview | null = null;
  selectedMaintenance: Maintenance | null = null;
  selectedCustomerDetails: Customer | null = null;
  billing: MaintenanceBilling | null = null;
  draftLines: MaintenanceBillingLine[] = [];
  billingNote = '';
  billingStatus: MaintenanceBillingStatus = MaintenanceBillingStatus.Open;
  newLine: MaintenanceBillingLine = this.createEmptyLine();
  invoiceDraft: InvoiceTemplateDraft = this.createEmptyInvoiceDraft();
  savingTemplate = false;

  constructor(
    private maintenanceService: MaintenanceService,
    private customerService: CustomerService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.loadOverview();
  }

  loadOverview(): void {
    this.loading = true;
    this.maintenanceService.getBillingOverview(this.filter).subscribe({
      next: (items) => {
        this.items = items ?? [];
        this.groups = this.buildCustomerGroups(this.items);
        if (this.selectedCustomer) {
          this.selectedCustomer = this.groups.find(group => group.key === this.selectedCustomer?.key) ?? null;
        }
        this.loading = false;
        if (this.selected && !this.items.some(item => item.maintenanceId === this.selected?.maintenanceId)) {
          this.clearSelection();
        }
        if (this.selectedCustomer == null && this.groups.length === 1) {
          this.selectCustomer(this.groups[0]);
        }
      },
      error: () => {
        this.loading = false;
        this.snackBar.open('Abrechnungsliste konnte nicht geladen werden', 'OK', { duration: 3500 });
      }
    });
  }

  selectItem(item: MaintenanceBillingOverview): void {
    this.selectedCustomer = this.groups.find(group => this.groupContains(group, item)) ?? this.selectedCustomer;
    this.selected = item;
    this.selectedLoading = true;
    this.maintenanceService.getMaintenance(item.maintenanceId).subscribe({
      next: (maintenance) => {
        this.selectedMaintenance = maintenance;
        this.loadBilling(item.maintenanceId);
      },
      error: () => {
        this.selectedLoading = false;
        this.snackBar.open('Auftrag konnte nicht geladen werden', 'OK', { duration: 3500 });
      }
    });
  }

  loadBilling(maintenanceId: number): void {
    this.maintenanceService.getBilling(maintenanceId).subscribe({
      next: (billing) => {
        this.billing = billing;
        this.billingStatus = billing.billingStatus;
        this.billingNote = billing.billingNote ?? '';
        this.draftLines = (billing.lines ?? []).map(line => ({ ...line }));
        this.newLine = this.createEmptyLine();
        this.selectedLoading = false;
      },
      error: () => {
        this.selectedLoading = false;
        this.snackBar.open('Rechnungspositionen konnten nicht geladen werden', 'OK', { duration: 3500 });
      }
    });
  }

  clearSelection(): void {
    this.selected = null;
    this.selectedMaintenance = null;
    this.billing = null;
    this.draftLines = [];
  }

  setFilter(filter: BillingFilter): void {
    this.filter = filter;
    this.loadOverview();
  }

  selectCustomer(group: CustomerBillingGroup): void {
    this.selectedCustomer = group;
    this.loadCustomerDetails(group);
    if (this.selected && !this.groupContains(group, this.selected)) {
      this.clearSelection();
    }
  }

  get visibleCustomerItems(): MaintenanceBillingOverview[] {
    if (!this.selectedCustomer) return [];
    return [...this.selectedCustomer.billable, ...this.selectedCustomer.pending];
  }

  get isSelectedCompleted(): boolean {
    return this.selected?.status === MaintenanceStatus.Completed;
  }

  addManualLine(): void {
    const description = this.newLine.description.trim();
    if (!description || this.newLine.quantity <= 0) return;

    this.draftLines.push({
      ...this.newLine,
      description,
      quantity: Number(this.newLine.quantity) || 1,
      unit: this.newLine.unit.trim() || 'Stk',
      unitPrice: Number(this.newLine.unitPrice) || 0,
      isIncluded: true,
      sortOrder: this.draftLines.length
    });
    this.newLine = this.createEmptyLine();
  }

  removeLine(index: number): void {
    this.draftLines.splice(index, 1);
    this.reindexLines();
  }

  moveLine(index: number, direction: -1 | 1): void {
    const next = index + direction;
    if (next < 0 || next >= this.draftLines.length) return;
    const [line] = this.draftLines.splice(index, 1);
    this.draftLines.splice(next, 0, line);
    this.reindexLines();
  }

  save(status = this.billingStatus): void {
    if (!this.selected) return;

    this.saving = true;
    this.reindexLines();
    this.maintenanceService.updateBilling(this.selected.maintenanceId, {
      billingStatus: status,
      billingNote: this.billingNote,
      lines: this.draftLines
    }).subscribe({
      next: (billing) => {
        this.billing = billing;
        this.billingStatus = billing.billingStatus;
        this.billingNote = billing.billingNote ?? '';
        this.draftLines = billing.lines.map(line => ({ ...line }));
        this.saving = false;
        this.snackBar.open('Abrechnung gespeichert', 'OK', { duration: 2200 });
        this.loadOverview();
      },
      error: () => {
        this.saving = false;
        this.snackBar.open('Abrechnung konnte nicht gespeichert werden', 'OK', { duration: 3500 });
      }
    });
  }

  async markBilledAndCreateInvoice(): Promise<void> {
    if (!this.isSelectedCompleted) {
      this.snackBar.open('Rechnung erst möglich, wenn der Einsatz abgeschlossen ist', 'OK', { duration: 3000 });
      return;
    }

    this.billingStatus = MaintenanceBillingStatus.Billed;
    await this.generateInvoicePdf();
    this.save(MaintenanceBillingStatus.Billed);
  }

  markOpen(): void {
    this.billingStatus = MaintenanceBillingStatus.Open;
    this.save(MaintenanceBillingStatus.Open);
  }

  async generateInvoicePdf(): Promise<void> {
    if (!this.selected) return;
    if (!this.isSelectedCompleted) {
      this.snackBar.open('Rechnung erst möglich, wenn der Einsatz abgeschlossen ist', 'OK', { duration: 3000 });
      return;
    }

    const customer = await this.ensureSelectedCustomerDetails();
    const includedLines = this.includedLines;
    const doc = new jsPDF();
    const today = new Date();
    let y = await drawPdfHeader(doc, 'Rechnung', `Rechnungsdatum: ${this.formatDate(today)}`);

    y = this.drawInvoiceAddressAndMeta(doc, y, customer, today);
    y = drawSectionTitle(doc, y, 'Positionen');
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Pos.', 14, y);
    doc.text('Beschreibung', 24, y);
    doc.text('Menge', 114, y);
    doc.text('ME', 138, y);
    doc.text('Einzelpreis', 150, y);
    doc.text('Gesamtpreis', 196, y, { align: 'right' });
    y += 5;
    doc.setDrawColor(220);
    doc.line(14, y, 196, y);
    y += 5;

    doc.setFont('helvetica', 'normal');
    includedLines.forEach((line, index) => {
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
      const total = this.getLineTotal(line);
      const descriptionLines = doc.splitTextToSize(line.description || '-', 86);
      doc.text(String(index + 1), 14, y);
      doc.text(descriptionLines, 24, y);
      doc.text(this.formatNumber(line.quantity), 114, y);
      doc.text(line.unit || '-', 138, y);
      doc.text(this.formatCurrency(line.unitPrice), 150, y);
      doc.text(this.formatCurrency(total), 196, y, { align: 'right' });
      y += Math.max(7, descriptionLines.length * 5);
    });

    y += 4;
    doc.setDrawColor(180);
    doc.line(112, y, 196, y);
    y += 8;
    const vat = Math.round(this.netTotal * 0.2 * 100) / 100;
    const gross = this.netTotal + vat;
    doc.setFont('helvetica', 'normal');
    doc.text('Rechnungssumme netto', 116, y);
    doc.text(this.formatCurrency(this.netTotal), 182, y, { align: 'right' });
    y += 7;
    doc.text('Umsatzsteuer 20,00%', 116, y);
    doc.text(this.formatCurrency(vat), 182, y, { align: 'right' });
    y += 7;
    doc.setDrawColor(120);
    doc.line(112, y, 196, y);
    y += 7;
    doc.setFont('helvetica', 'bold');
    doc.text('Rechnungsbetrag', 116, y);
    doc.text(this.formatCurrency(gross), 182, y, { align: 'right' });

    y += 14;
    y = this.drawInvoiceFooterDetails(doc, y, customer);
    if (this.invoiceDraft.includeServiceAppendix) {
      this.addServiceOverviewAppendix(doc);
    }

    doc.save(`Rechnung_${this.selected.maintenanceId}.pdf`);
  }

  get includedLines(): MaintenanceBillingLine[] {
    return this.draftLines.filter(line => line.isIncluded);
  }

  get netTotal(): number {
    return this.includedLines.reduce((sum, line) => sum + this.getLineTotal(line), 0);
  }

  get unpricedIncludedLines(): number {
    return this.includedLines.filter(line => Number(line.unitPrice) <= 0).length;
  }

  getLineTotal(line: MaintenanceBillingLine): number {
    return (Number(line.quantity) || 0) * (Number(line.unitPrice) || 0);
  }

  formatCurrency(value: number): string {
    return value.toLocaleString('de-AT', { style: 'currency', currency: 'EUR' });
  }

  getStatusLabel(item: MaintenanceBillingOverview): string {
    switch (item.status) {
      case MaintenanceStatus.Completed: return 'Abgeschlossen';
      case MaintenanceStatus.InProgress: return 'In Arbeit';
      case MaintenanceStatus.Planned: return 'Geplant';
      case MaintenanceStatus.Overdue: return 'Überfällig';
      case MaintenanceStatus.Cancelled: return 'Storniert';
      default: return item.status;
    }
  }

  getSourceLabel(line: MaintenanceBillingLine): string {
    switch (line.sourceType) {
      case MaintenanceBillingLineSourceType.Material: return 'Material';
      case MaintenanceBillingLineSourceType.Regie: return 'Regie';
      default: return 'Manuell';
    }
  }

  get customerContacts(): CustomerContactPerson[] {
    return this.selectedCustomerDetails?.contactPersons ?? [];
  }

  applyInvoiceContact(): void {
    const contact = this.findContact(this.invoiceDraft.invoiceContactId);
    if (!contact) return;
    this.invoiceDraft.invoiceName = contact.fullName || this.invoiceDraft.invoiceName;
    this.invoiceDraft.invoiceAddress = contact.address || this.invoiceDraft.invoiceAddress;
    this.invoiceDraft.invoiceEmail = contact.email || this.invoiceDraft.invoiceEmail;
  }

  applyShippingContact(): void {
    const contact = this.findContact(this.invoiceDraft.shippingContactId);
    if (!contact) return;
    this.invoiceDraft.shippingName = contact.fullName || this.invoiceDraft.shippingName;
    this.invoiceDraft.shippingAddress = contact.address || this.invoiceDraft.shippingAddress;
  }

  applyProjectContact(): void {
    const contact = this.findContact(this.invoiceDraft.projectContactId);
    if (!contact) return;
    this.invoiceDraft.projectManagerName = contact.fullName || this.invoiceDraft.projectManagerName;
    this.invoiceDraft.projectManagerPhone = contact.phone || this.invoiceDraft.projectManagerPhone;
    this.invoiceDraft.projectManagerEmail = contact.email || this.invoiceDraft.projectManagerEmail;
  }

  saveInvoiceTemplate(): void {
    if (!this.selectedCustomerDetails?.id || this.savingTemplate) return;

    this.savingTemplate = true;
    const updated: Customer = {
      ...this.selectedCustomerDetails,
      invoiceName: this.invoiceDraft.invoiceName,
      invoiceAddress: this.invoiceDraft.invoiceAddress,
      shippingName: this.invoiceDraft.shippingName,
      shippingAddress: this.invoiceDraft.shippingAddress,
      customerNumber: this.invoiceDraft.customerNumber,
      vatId: this.invoiceDraft.vatId,
      orderNumber: this.invoiceDraft.orderNumber,
      orderDate: this.invoiceDraft.orderDate,
      projectNumber: this.invoiceDraft.projectNumber,
      projectManagerName: this.invoiceDraft.projectManagerName,
      projectManagerPhone: this.invoiceDraft.projectManagerPhone,
      projectManagerEmail: this.invoiceDraft.projectManagerEmail,
      invoiceEmail: this.invoiceDraft.invoiceEmail,
      paymentTerms: this.invoiceDraft.paymentTerms,
      deliveryTerms: this.invoiceDraft.deliveryTerms,
      invoiceNote: this.invoiceDraft.invoiceNote,
      contactPersons: this.selectedCustomerDetails.contactPersons ?? []
    };

    this.customerService.updateCustomer(updated.id, updated).subscribe({
      next: (customer) => {
        this.selectedCustomerDetails = customer;
        this.invoiceDraft = this.buildInvoiceDraft(customer);
        this.savingTemplate = false;
        this.snackBar.open('Rechnungsvorlage gespeichert', 'OK', { duration: 2600 });
      },
      error: () => {
        this.savingTemplate = false;
        this.snackBar.open('Rechnungsvorlage konnte nicht gespeichert werden', 'OK', { duration: 3500 });
      }
    });
  }

  trackByCustomerGroup(_: number, group: CustomerBillingGroup): string {
    return group.key;
  }

  trackByBillingItem(_: number, item: MaintenanceBillingOverview): number {
    return item.maintenanceId;
  }

  trackByLine(index: number, line: MaintenanceBillingLine): string {
    return `${line.sourceType}-${line.sourceId ?? line.id ?? index}`;
  }

  trackByContact(index: number, contact: CustomerContactPerson): number {
    return contact.id ?? index;
  }

  private loadCustomerDetails(group: CustomerBillingGroup): void {
    this.selectedCustomerDetails = null;
    if (!group.customerId) return;

    this.customerService.getCustomer(group.customerId).subscribe({
      next: (customer) => {
        if (this.selectedCustomer?.customerId === group.customerId) {
          this.selectedCustomerDetails = customer;
          this.invoiceDraft = this.buildInvoiceDraft(customer);
        }
      },
      error: () => {
        this.selectedCustomerDetails = null;
      }
    });
  }

  private async ensureSelectedCustomerDetails(): Promise<Customer | null> {
    if (this.selectedCustomerDetails) return this.selectedCustomerDetails;
    const customerId = this.selectedCustomer?.customerId ?? this.selected?.customerId;
    if (!customerId) return null;

    try {
      this.selectedCustomerDetails = await firstValueFrom(this.customerService.getCustomer(customerId));
      this.invoiceDraft = this.buildInvoiceDraft(this.selectedCustomerDetails);
      return this.selectedCustomerDetails;
    } catch {
      this.snackBar.open('Rechnungsdaten des Kunden konnten nicht geladen werden', 'OK', { duration: 3500 });
      return null;
    }
  }

  private drawInvoiceAddressAndMeta(doc: jsPDF, y: number, customer: Customer | null, invoiceDate: Date): number {
    const recipientName = this.textOrFallback(this.invoiceDraft.invoiceName || customer?.invoiceName, this.selected?.customerName || 'Kunde');
    const recipientAddress = this.textOrFallback(this.invoiceDraft.invoiceAddress || customer?.invoiceAddress, customer?.address || '');
    const shippingName = this.textOrFallback(this.invoiceDraft.shippingName || customer?.shippingName, recipientName);
    const shippingAddress = this.textOrFallback(this.invoiceDraft.shippingAddress || customer?.shippingAddress, recipientAddress);

    doc.setTextColor(20, 24, 38);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('ELIN GmbH, Emil-Rathenau-Straße 4, 4030 Linz', 14, y);
    y += 10;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(recipientName, 14, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    y = this.drawMultiline(doc, this.addressLines(recipientAddress), 14, y, 76);

    const metaStartY = y - Math.max(18, this.addressLines(recipientAddress).length * 5);
    let metaY = metaStartY;
    doc.setFontSize(9);
    metaY = this.drawMetaRow(doc, 'Rechnungs-Nr.', this.buildInvoiceNumber(), 112, metaY, true);
    metaY = this.drawMetaRow(doc, 'Rechnungsdatum', this.formatDate(invoiceDate), 112, metaY, true);
    metaY = this.drawMetaRow(doc, 'Kunden-Nr.', this.invoiceDraft.customerNumber || customer?.customerNumber, 112, metaY, true);
    metaY = this.drawMetaRow(doc, 'UID-Kunde', this.invoiceDraft.vatId || customer?.vatId, 112, metaY, true);
    metaY += 4;
    metaY = this.drawMetaRow(doc, 'Ihre Bestellung', this.invoiceDraft.orderNumber || customer?.orderNumber, 112, metaY);
    metaY = this.drawMetaRow(doc, 'Bestelldatum', this.invoiceDraft.orderDate ? this.formatDate(this.invoiceDraft.orderDate) : '', 112, metaY);
    metaY = this.drawMetaRow(doc, 'Projekt-Nr.', this.invoiceDraft.projectNumber || customer?.projectNumber, 112, metaY);
    metaY = this.drawMetaRow(doc, 'Projektleiter', this.invoiceDraft.projectManagerName || customer?.projectManagerName, 112, metaY);
    metaY = this.drawMetaRow(doc, 'Telefon', this.invoiceDraft.projectManagerPhone || customer?.projectManagerPhone, 112, metaY);
    metaY = this.drawMetaRow(doc, 'E-Mail', this.invoiceDraft.projectManagerEmail || customer?.projectManagerEmail || customer?.invoiceEmail, 112, metaY);

    y = Math.max(y + 12, metaY + 2);
    doc.setFont('helvetica', 'bold');
    doc.text('Versandanschrift / Versandangaben', 14, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.text(shippingName, 14, y);
    y += 5;
    y = this.drawMultiline(doc, this.addressLines(shippingAddress), 14, y, 76);

    y += 10;
    doc.setFont('helvetica', 'normal');
    doc.text('Leistungszeitraum:', 14, y);
    doc.text(this.getServicePeriod(), 50, y);
    return y + 10;
  }

  private drawInvoiceFooterDetails(doc: jsPDF, y: number, customer: Customer | null): number {
    const deliveryTerms = this.normalizeText(this.invoiceDraft.deliveryTerms || customer?.deliveryTerms);
    const paymentTerms = this.normalizeText(this.invoiceDraft.paymentTerms || customer?.paymentTerms);
    const invoiceNote = this.normalizeText(this.invoiceDraft.invoiceNote || customer?.invoiceNote);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');

    if (deliveryTerms) {
      doc.text('Lieferbedingungen:', 14, y);
      doc.text(deliveryTerms, 52, y);
      y += 6;
    }

    if (paymentTerms) {
      doc.text('Zahlungsbedingungen:', 14, y);
      doc.text(paymentTerms, 52, y);
      y += 6;
    }

    if (invoiceNote) {
      y += 2;
      y = drawSectionTitle(doc, y, 'Hinweis');
      y = this.drawMultiline(doc, doc.splitTextToSize(invoiceNote, 176), 14, y, 176);
    }

    return y;
  }

  private addServiceOverviewAppendix(doc: jsPDF): void {
    if (!this.selected || !this.selectedMaintenance) return;

    const maintenance = this.selectedMaintenance;
    doc.addPage();
    let y = 20;
    doc.setTextColor(20, 24, 38);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('Anhang: Übersicht des abgerechneten Einsatzes', 14, y);
    y += 10;

    y = drawSectionTitle(doc, y, 'Einsatzdaten');
    const technicianNames = (maintenance.assignedUsers ?? [])
      .map(user => user.userName || user.email)
      .filter(Boolean)
      .join(', ');
    const completedDate = maintenance.completedOn || this.selected.completedOn;
    const dataRows: Array<[string, string]> = [
      ['Einsatz-Nr.', String(maintenance.id)],
      ['Titel', maintenance.title],
      ['Kunde', this.selected.customerName || '-'],
      ['Anlage', this.selected.equipmentName || '-'],
      ['Geplant am', this.formatDate(maintenance.scheduledDate)],
      ['Abgeschlossen am', completedDate ? this.formatDate(completedDate) : '-'],
      ['Techniker', technicianNames || maintenance.assignedUser?.userName || maintenance.assignedEmployee?.fullName || '-'],
      ['Abfahrt Büro', maintenance.departureFromOffice || '-'],
      ['Ankunft Kunde', maintenance.arrivalTime || '-'],
      ['Abfahrt Kunde', maintenance.departureTime || '-'],
      ['Rückkehr Büro', maintenance.returnToOffice || '-'],
      ['Kundenfreigabe', maintenance.customerAcknowledged ? 'Ja' : 'Nein']
    ];

    for (const [label, value] of dataRows) {
      y = this.ensurePdfSpace(doc, y, 7);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text(label, 14, y);
      doc.setFont('helvetica', 'normal');
      doc.text(doc.splitTextToSize(value || '-', 126), 58, y);
      y += Math.max(6, doc.splitTextToSize(value || '-', 126).length * 5);
    }

    const workItems = maintenance.workItems ?? [];
    const problems = maintenance.problems ?? [];
    if (maintenance.initialSituation || workItems.length > 0 || maintenance.notes) {
      y += 4;
      y = this.ensurePdfSpace(doc, y, 22);
      y = drawSectionTitle(doc, y, 'Leistungsbeschreibung');
      if (maintenance.initialSituation) {
        y = this.drawLabeledBlock(doc, y, 'Ausgangslage', maintenance.initialSituation);
      }
      if (workItems.length > 0) {
        y = this.drawLabeledBlock(doc, y, 'Durchgeführte Arbeiten', workItems.join('\n'));
      }
      if (maintenance.notes) {
        y = this.drawLabeledBlock(doc, y, 'Notizen', maintenance.notes);
      }
    }

    if (problems.length > 0) {
      y += 4;
      y = this.ensurePdfSpace(doc, y, 22);
      y = drawSectionTitle(doc, y, 'Probleme und Lösungen');
      for (const problem of problems) {
        const text = `${problem.description || '-'}${problem.solution ? `\nLösung: ${problem.solution}` : ''}`;
        y = this.drawLabeledBlock(doc, y, 'Eintrag', text);
      }
    }

    y += 4;
    y = this.ensurePdfSpace(doc, y, 28);
    y = drawSectionTitle(doc, y, 'Abgerechnete Positionen');
    for (const line of this.includedLines) {
      y = this.ensurePdfSpace(doc, y, 8);
      const total = this.getLineTotal(line);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(doc.splitTextToSize(line.description || '-', 100), 14, y);
      doc.text(`${this.formatNumber(line.quantity)} ${line.unit || ''}`, 120, y);
      doc.text(this.formatCurrency(total), 182, y, { align: 'right' });
      y += Math.max(6, doc.splitTextToSize(line.description || '-', 100).length * 5);
    }

    const regieLines = maintenance.regieLines ?? [];
    if (regieLines.length > 0) {
      y += 4;
      y = this.ensurePdfSpace(doc, y, 26);
      y = drawSectionTitle(doc, y, 'Regiestunden');
      for (const line of regieLines) {
        y = this.ensurePdfSpace(doc, y, 7);
        doc.setFont('helvetica', 'normal');
        doc.text(line.label || 'Regiestunden', 14, y);
        doc.text(`${this.formatNumber(line.hours)} h`, 92, y);
        doc.text(this.formatCurrency(line.hourlyRate), 118, y);
        doc.text(line.isCustomerAcknowledged ? 'unterschrieben' : 'offen', 150, y);
        y += 6;
      }
    }
  }

  private drawLabeledBlock(doc: jsPDF, y: number, label: string, text: string): number {
    y = this.ensurePdfSpace(doc, y, 14);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(label, 14, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    return this.drawMultiline(doc, doc.splitTextToSize(text, 176), 14, y, 176) + 2;
  }

  private drawMultiline(doc: jsPDF, lines: string[], x: number, y: number, _maxWidth: number): number {
    for (const line of lines.length > 0 ? lines : ['-']) {
      y = this.ensurePdfSpace(doc, y, 6);
      doc.text(line, x, y);
      y += 5;
    }
    return y;
  }

  private drawMetaRow(doc: jsPDF, label: string, value: string | Date | null | undefined, x: number, y: number, bold = false): number {
    const text = this.normalizeText(value);
    if (!text) return y;

    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.text(label, x, y);
    doc.text(text, x + 34, y);
    return y + 5;
  }

  private ensurePdfSpace(doc: jsPDF, y: number, needed: number): number {
    if (y + needed <= 282) return y;
    doc.addPage();
    return 20;
  }

  private createEmptyInvoiceDraft(): InvoiceTemplateDraft {
    return {
      invoiceName: '',
      invoiceAddress: '',
      shippingName: '',
      shippingAddress: '',
      customerNumber: '',
      vatId: '',
      orderNumber: '',
      orderDate: null,
      projectNumber: '',
      projectManagerName: '',
      projectManagerPhone: '',
      projectManagerEmail: '',
      invoiceEmail: '',
      paymentTerms: '',
      deliveryTerms: '',
      invoiceNote: '',
      invoiceContactId: null,
      shippingContactId: null,
      projectContactId: null,
      includeServiceAppendix: true
    };
  }

  private buildInvoiceDraft(customer: Customer | null): InvoiceTemplateDraft {
    const invoiceContact = this.pickContact(customer, 'invoice');
    const shippingContact = this.pickContact(customer, 'shipping');
    const projectContact = this.pickContact(customer, 'project');

    return {
      invoiceName: invoiceContact?.fullName || customer?.invoiceName || customer?.name || '',
      invoiceAddress: invoiceContact?.address || customer?.invoiceAddress || customer?.address || '',
      shippingName: shippingContact?.fullName || customer?.shippingName || invoiceContact?.fullName || customer?.invoiceName || customer?.name || '',
      shippingAddress: shippingContact?.address || customer?.shippingAddress || invoiceContact?.address || customer?.invoiceAddress || customer?.address || '',
      customerNumber: customer?.customerNumber || '',
      vatId: customer?.vatId || '',
      orderNumber: customer?.orderNumber || '',
      orderDate: customer?.orderDate ? new Date(customer.orderDate).toISOString().slice(0, 10) : null,
      projectNumber: customer?.projectNumber || '',
      projectManagerName: projectContact?.fullName || customer?.projectManagerName || '',
      projectManagerPhone: projectContact?.phone || customer?.projectManagerPhone || '',
      projectManagerEmail: projectContact?.email || customer?.projectManagerEmail || '',
      invoiceEmail: invoiceContact?.email || customer?.invoiceEmail || '',
      paymentTerms: customer?.paymentTerms || '',
      deliveryTerms: customer?.deliveryTerms || '',
      invoiceNote: customer?.invoiceNote || '',
      invoiceContactId: invoiceContact?.id ?? null,
      shippingContactId: shippingContact?.id ?? null,
      projectContactId: projectContact?.id ?? null,
      includeServiceAppendix: true
    };
  }

  private pickContact(customer: Customer | null, role: 'invoice' | 'shipping' | 'project'): CustomerContactPerson | null {
    const contacts = customer?.contactPersons ?? [];
    if (role === 'invoice') {
      return contacts.find(contact => contact.isInvoiceRecipient) ?? null;
    }
    if (role === 'shipping') {
      return contacts.find(contact => contact.isShippingRecipient) ?? null;
    }
    return contacts.find(contact => contact.isProjectManager)
      ?? contacts.find(contact => contact.isCommercialContact)
      ?? null;
  }

  private findContact(contactId: number | null): CustomerContactPerson | null {
    if (contactId == null) return null;
    return this.customerContacts.find(contact => contact.id === contactId) ?? null;
  }

  private addressLines(value: string): string[] {
    return this.normalizeText(value)
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(Boolean);
  }

  private textOrFallback(value: string | null | undefined, fallback: string): string {
    return this.normalizeText(value) || fallback;
  }

  private normalizeText(value: string | Date | null | undefined): string {
    if (value == null) return '';
    if (value instanceof Date) return this.formatDate(value);
    return String(value).trim();
  }

  private formatNumber(value: number): string {
    return (Number(value) || 0).toLocaleString('de-AT', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  private formatDate(value: string | Date): string {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('de-AT');
  }

  private buildInvoiceNumber(): string {
    const year = new Date().getFullYear();
    return `${year}-${String(this.selected?.maintenanceId ?? 0).padStart(5, '0')}`;
  }

  private getServicePeriod(): string {
    const source = this.selected?.completedOn || this.selected?.scheduledDate;
    if (!source) return '-';
    const date = new Date(source);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('de-AT', { month: 'long', year: 'numeric' });
  }

  private createEmptyLine(): MaintenanceBillingLine {
    return {
      sourceType: MaintenanceBillingLineSourceType.Manual,
      sourceId: null,
      description: '',
      quantity: 1,
      unit: 'Stk',
      unitPrice: 0,
      isIncluded: true,
      sortOrder: 0,
      totalAmount: 0
    };
  }

  private reindexLines(): void {
    this.draftLines.forEach((line, index) => line.sortOrder = index);
  }

  private buildCustomerGroups(items: MaintenanceBillingOverview[]): CustomerBillingGroup[] {
    const map = new Map<string, CustomerBillingGroup>();

    for (const item of items) {
      const key = item.customerId != null ? `customer-${item.customerId}` : `name-${item.customerName || 'ohne-kunde'}`;
      let group = map.get(key);
      if (!group) {
        group = {
          key,
          customerId: item.customerId,
          customerName: item.customerName || 'Ohne Kunde',
          billable: [],
          pending: [],
          totalNet: 0,
          openCount: 0,
          billedCount: 0,
          unpricedCount: 0
        };
        map.set(key, group);
      }

      if (item.status === MaintenanceStatus.Completed) {
        group.billable.push(item);
      } else {
        group.pending.push(item);
      }

      group.totalNet += Number(item.netTotal) || 0;
      group.unpricedCount += item.unpricedLineCount || 0;
      if (item.billingStatus === MaintenanceBillingStatus.Billed) {
        group.billedCount++;
      } else {
        group.openCount++;
      }
    }

    return Array.from(map.values())
      .map(group => ({
        ...group,
        billable: group.billable.sort(this.sortBillingItems),
        pending: group.pending.sort(this.sortBillingItems)
      }))
      .sort((a, b) => {
        const billableDiff = b.billable.length - a.billable.length;
        if (billableDiff !== 0) return billableDiff;
        return a.customerName.localeCompare(b.customerName, 'de-AT');
      });
  }

  private groupContains(group: CustomerBillingGroup, item: MaintenanceBillingOverview): boolean {
    return group.billable.some(entry => entry.maintenanceId === item.maintenanceId)
      || group.pending.some(entry => entry.maintenanceId === item.maintenanceId);
  }

  private sortBillingItems(a: MaintenanceBillingOverview, b: MaintenanceBillingOverview): number {
    return new Date(b.completedOn || b.scheduledDate).getTime() - new Date(a.completedOn || a.scheduledDate).getTime();
  }
}
